import { useNavigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Quote, ArrowRight } from "lucide-react";

const STEPS = [
  { n: "01", title: "Describe your lesson", desc: "Enter a topic, grade level, and standard. The AI understands your learning objective and builds a structured VR scene around it." },
  { n: "02", title: "AI generates the environment", desc: "VividVR creates 3D models, spatial audio, interactive elements, and a narrative arc — fully rendered and ready to explore." },
  { n: "03", title: "Customize with no code", desc: "Adjust difficulty, swap assets, add quizzes, or embed questions using a visual builder. No coding experience required." },
  { n: "04", title: "Deploy to students", desc: "Assign lessons to class sections, track engagement, and review performance data — all from a single educator dashboard." },
];
const STATS = [
  { n: "90%", l: "Faster lesson creation" },
  { n: "47K+", l: "Students reached" },
  { n: "12", l: "Districts onboarded" },
];

export default function HowItWorks() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-center max-w-3xl mx-auto animate-in-up">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">How It Works</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            From concept to immersive lesson in four steps
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Traditional VR content production takes months and specialized teams. VividVR compresses that pipeline into a
            single workflow any educator can run in an afternoon.
          </p>
        </div>

        <div className="mt-14 space-y-5">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`hiw-step-${i + 1}`} className="flex flex-col sm:flex-row gap-5 sm:gap-8 rounded-3xl border border-border bg-card p-7 items-start">
              <div className="text-5xl font-display font-extrabold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent shrink-0 leading-none">{s.n}</div>
              <div>
                <h3 className="text-xl font-display font-semibold">{s.title}</h3>
                <p className="text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14 mt-14">
          <Quote className="w-9 h-9 text-accent" />
          <p className="text-xl sm:text-2xl font-display font-semibold leading-snug mt-4">
            “We used to wait six months for a single VR lesson to be produced. Now our science team generates a new
            simulation for each unit the night before they teach it.”
          </p>
          <div className="mt-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center font-bold">ML</div>
            <div><div className="font-semibold">Maria Ledesma</div><div className="text-white/70 text-sm">Director of Instructional Technology, Riverside USD</div></div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-10">
            {STATS.map((s) => (
              <div key={s.l} className="text-center">
                <div className="text-3xl sm:text-4xl font-display font-extrabold">{s.n}</div>
                <div className="text-white/70 text-xs sm:text-sm mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-14">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Try it with your lesson</h2>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="hiw-try-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-8">Generate a lesson <ArrowRight className="w-4 h-4 ml-1" /></Button>
            <Button size="lg" variant="outline" onClick={() => nav("/features")} className="rounded-full h-12 px-8">Explore features</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
